import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Badge, Checkbox, Label, PickerDialog, SearchableSelect, formatCount, } from '@wit-support/ui';
import { EntityId } from '@wit-support/ui-domain';
import { Trophy } from 'lucide-react';
import { listPosts } from '../api.js';
import { ADS_QUERY_DEFAULTS } from '../query-policy.js';
import { formatMinor } from '../format.js';
import { MediaPreview } from './media-preview.js';
import { AddPickedButton, PickerResults, usePicked } from './picker-parts.js';
/**
 * Заголовок поста — его текст (одной строкой, без лишних пробелов); текста
 * нет — «Пост без текста». Id поста — не текстом: в подсказке и кнопке «Скопировать ID» (`EntityId`).
 */
export function postTitle(text) {
    const t = text?.replace(/\s+/g, ' ').trim();
    return t ? t : 'Пост без текста';
}
/** Картинка поста (через admin-api) или заглушка «нет картинки». */
export function PostThumb({ post }) {
    const t = post.text?.replace(/\s+/g, ' ').trim();
    return (_jsx(MediaPreview, { previewUrl: post.previewUrl, kind: post.kind === 'unknown' ? 'unknown' : post.kind, label: t ? `Пост: ${t.length > 60 ? `${t.slice(0, 60)}…` : t}` : `Пост ${post.postId}` }));
}
/** «7 покупок · CPP 22,40 $ · за 3 дня». */
export function postWinnerText(w, currency) {
    return `${formatCount(w.purchases, ['покупка', 'покупки', 'покупок'])} · CPP ${formatMinor(w.cppMinor, currency)} · за ${formatCount(w.windowDays, ['день', 'дня', 'дней'])}`;
}
/** «Клонов: 3 (живых 1, чистый скейл 0)» / «Клонов нет». */
export function postClonesText(c) {
    if (c.total === 0)
        return 'Клонов нет';
    return `Клонов: ${c.total} (живых ${c.live}, чистый скейл ${c.autoClean})`;
}
/** Итоги поста: покупки, CPP и клоны — коротко. */
export function PostStats({ post }) {
    return (_jsxs("div", { className: "flex flex-wrap gap-1 text-xs", children: [post.winner ? (_jsxs(Badge, { variant: "success", className: "max-w-full flex-wrap", children: [_jsx(Trophy, { "aria-hidden": true, className: "h-3 w-3" }), postWinnerText(post.winner, post.currency)] })) : null, _jsx(Badge, { variant: "neutral", className: "max-w-full flex-wrap", children: postClonesText(post.clones) })] }));
}
/**
 * Пост глазами каждого запрошенного кабинета. Новый auto-ads — `accounts[]`;
 * чуть более старый — `pageMatch[]`; без обоих — только кабинет-источник.
 */
export function postAccountMatches(post, accountIds) {
    if (post.accounts != null) {
        const by = new Map(post.accounts.map((a) => [a.platformAccountId, a]));
        return accountIds.map((id) => ({
            platformAccountId: id,
            inIndex: by.get(id)?.inIndex ?? null,
            pageMatch: by.get(id)?.pageMatch ?? null,
        }));
    }
    const pm = new Map((post.pageMatch ?? []).map((m) => [m.platformAccountId, m.matches]));
    return accountIds.map((id) => ({
        platformAccountId: id,
        inIndex: post.platformAccountId != null ? post.platformAccountId === id : null,
        pageMatch: pm.get(id) ?? null,
    }));
}
/** Бейджи «страница поста ↔ кабинет»: ✓ своя страница, ✗ чужая, ? — профиля нет. */
export function PostAccountBadges({ post, accountIds, accountNames, }) {
    const matches = postAccountMatches(post, accountIds);
    return (_jsx("div", { className: "flex flex-wrap gap-1 text-xs", "data-testid": `post-accounts-${post.objectStoryId}`, children: matches.map((m) => {
            const name = accountNames.get(m.platformAccountId) ?? m.platformAccountId;
            return m.pageMatch === true ? (_jsxs(Badge, { variant: "success", "data-match": "yes", children: ["\u2713 ", name] }, m.platformAccountId)) : m.pageMatch === false ? (_jsxs(Badge, { variant: "danger", "data-match": "no", title: "\u0421\u0442\u0440\u0430\u043D\u0438\u0446\u0430 \u043F\u043E\u0441\u0442\u0430 \u043D\u0435 \u0442\u0430, \u0447\u0442\u043E \u0432 \u043F\u0440\u043E\u0444\u0438\u043B\u0435 \u043A\u0430\u0431\u0438\u043D\u0435\u0442\u0430: \u0432 \u044D\u0442\u043E\u043C \u043A\u0430\u0431\u0438\u043D\u0435\u0442\u0435 Meta \u043F\u043E\u0441\u0442 \u043D\u0435 \u0437\u0430\u043F\u0443\u0441\u0442\u0438\u0442", children: ["\u2717 ", name, ": \u0447\u0443\u0436\u0430\u044F \u0441\u0442\u0440\u0430\u043D\u0438\u0446\u0430"] }, m.platformAccountId)) : (_jsxs(Badge, { variant: "neutral", "data-match": "unknown", title: "\u0421\u0442\u0440\u0430\u043D\u0438\u0446\u0430 \u043F\u0440\u043E\u0444\u0438\u043B\u044F \u043A\u0430\u0431\u0438\u043D\u0435\u0442\u0430 \u043D\u0435 \u0437\u0430\u0434\u0430\u043D\u0430 \u2014 \u043F\u0440\u043E\u0432\u0435\u0440\u0438\u0442 preview", children: ["? ", name] }, m.platformAccountId));
        }) }));
}
const ALL = '__all__';
/**
 * «Топ-посты» выбранных кабинетов одним списком: превью, покупки, CPP, клоны
 * и — при нескольких кабинетах — совпадение страницы поста с каждым. Фильтр
 * «Кабинет» сужает список до постов одного кабинета (по умолчанию — тот, что
 * выбран переключателем блока, если его трогали; иначе все).
 */
export function TopPostsDialog({ open, onOpenChange, accountIds, accountNames, initialFilter = null, onPick, }) {
    const picked = usePicked((p) => p.objectStoryId);
    const [filterPick, setFilter] = useState(null);
    const filter = filterPick ??
        (initialFilter != null && accountIds.includes(initialFilter) ? initialFilter : ALL);
    const ids = [...accountIds];
    const q = useQuery({
        ...ADS_QUERY_DEFAULTS,
        queryKey: ['ads', 'posts', ids.join(','), 'winner'],
        queryFn: () => listPosts({ platformAccountIds: ids, sort: 'winner', limit: 30 }),
        enabled: open && ids.length > 0,
    });
    const all = q.data?.items ?? [];
    const sourceOf = (p) => p.platformAccountId ??
        p.accounts?.find((a) => a.inIndex === true)?.platformAccountId ??
        (ids.length === 1 ? (ids[0] ?? null) : null);
    const items = filter === ALL ? all : all.filter((p) => sourceOf(p) === filter);
    const many = ids.length > 1;
    const single = ids.length === 1 ? (accountNames.get(ids[0] ?? '') ?? null) : null;
    return (_jsxs(PickerDialog, { open: open, onOpenChange: onOpenChange, onReset: () => {
            picked.reset();
            setFilter(null);
        }, size: "lg", contentClassName: "flex flex-col", title: single
            ? `Топ-посты кабинета «${single}»`
            : many
                ? 'Топ-посты кабинетов'
                : 'Топ-посты кабинета', description: many
            ? 'Посты-победители всех выбранных кабинетов: покупки, цена покупки, клоны и с какими кабинетами совпадает страница поста.'
            : 'Посты-победители: покупки, цена покупки и сколько раз пост уже клонировали.', footer: _jsx(AddPickedButton, { count: picked.picked.length, onClick: () => {
                onPick(picked.picked.map((p) => ({ post: p, sourceAccountId: sourceOf(p) })));
                picked.reset();
                onOpenChange(false);
            } }), children: [many ? (_jsxs("div", { className: "mb-3 flex flex-wrap items-center gap-2", "data-testid": "top-posts-filter", children: [_jsx(Label, { htmlFor: "top-posts-account", children: "\u041A\u0430\u0431\u0438\u043D\u0435\u0442" }), _jsx(SearchableSelect, { id: "top-posts-account", className: "w-56", value: filter, onChange: setFilter, options: [
                            { value: ALL, label: 'Все кабинеты' },
                            ...ids.map((id) => ({
                                value: id,
                                label: accountNames.get(id) ?? id,
                                keywords: [id],
                            })),
                        ] })] })) : null, _jsx(PickerResults, { error: q.isError ? q.error : null, loading: q.isLoading, empty: items.length === 0, emptyState: {
                    icon: Trophy,
                    title: 'Постов нет',
                    description: many
                        ? 'В индексе выбранных кабинетов постов нет.'
                        : 'В индексе кабинета пока нет постов.',
                }, children: _jsx("ul", { className: "flex flex-col gap-2", "data-testid": "top-posts", children: items.map((p) => {
                        const id = `top-post-${p.objectStoryId}`;
                        const source = sourceOf(p);
                        return (_jsxs("li", { className: "flex items-start gap-3", children: [_jsx(Checkbox, { id: id, checked: picked.has(p), onCheckedChange: (v) => picked.toggle(p, v === true) }), _jsx(PostThumb, { post: p }), _jsxs("div", { className: "flex min-w-0 flex-col gap-1", children: [_jsxs("span", { className: "group/entity flex min-w-0 items-start gap-1", children: [_jsx(Label, { htmlFor: id, className: "line-clamp-2 min-w-0 text-sm font-medium [overflow-wrap:anywhere]", children: postTitle(p.text) }), _jsx(EntityId, { id: p.objectStoryId, kind: "post", className: "mt-0.5" })] }), many && source != null ? (_jsxs("span", { className: "text-xs text-[var(--color-text-muted)]", children: ["\u0438\u0437 \u043A\u0430\u0431\u0438\u043D\u0435\u0442\u0430 ", accountNames.get(source) ?? source] })) : null, _jsx(PostStats, { post: p }), many ? (_jsx(PostAccountBadges, { post: p, accountIds: ids, accountNames: accountNames })) : null] })] }, p.objectStoryId));
                    }) }) })] }));
}
//# sourceMappingURL=top-posts-dialog.js.map